import PremiumAccessButton from '@/app/[locale]/[roomId]/components/PremiumAccessButton'
import CloseIcon from '@/components/assets/icon_close.svg'
import IconDiamond from '@/components/assets/icon_diamond.svg'
import WarningIcon from '@/components/assets/icon_warning.svg'
import { Button } from '@/components/ui/button'
import { useCheckEnterRoomContext } from '@/context/CheckEnterRoomContext'
import { useUAContext } from '@/context/UAContext'
import { cn } from '@/lib/utils'
import { useTranslations } from 'next-intl'
import { ReactNode } from 'react'
import { useIsClient } from 'usehooks-ts'

const RenderPremiumButton = () => {
  const isClient = useIsClient()
  const { roomData, isVoucherCheckPassed } = useCheckEnterRoomContext()
  const { isMobileApp, isMobile } = useUAContext()

  return (
    isClient &&
    !isMobileApp &&
    !isMobile && (
      <PremiumAccessButton
        isPaid={isVoucherCheckPassed}
        page="detail"
        communityUserId={roomData.owner.community_user_id}
      />
    )
  )
}

type AlertVariant = 'device' | 'premium'
type AlertConfig = {
  icon: ReactNode
  message: ReactNode
  children?: () => ReactNode
}

const getAlertConfig = (
  variant: AlertVariant,
  t: ReturnType<typeof useTranslations>,
): AlertConfig => {
  switch (variant) {
    case 'device':
      return {
        icon: <WarningIcon />,
        message: t.rich('trailer-carousel.device-alert-overlay-description', {
          alertTitle: t('trailer-carousel.device-alert-title'),
          title: (chunks: ReactNode) => <span className="subheader4-omni-styles">{chunks}</span>,
        }),
      }
    case 'premium':
      return {
        icon: <IconDiamond className="fill-[#FF82FB]" />,
        message: t.rich('trailer-carousel.premium-alert-overlay-description', {
          alertTitle: t('trailer-carousel.premium-alert-title'),
          title: (chunks: ReactNode) => (
            <span className="subheader4-omni-styles bg-gradient-title-3 bg-clip-text text-transparent">
              {chunks}
            </span>
          ),
        }),
        children: () => <RenderPremiumButton />,
      }
  }
}

type AlertOverlayProps = {
  onClose: () => void
  variant: AlertVariant
  className?: string
}

const AlertOverlay = ({ onClose, variant, className }: AlertOverlayProps) => {
  const { isMobileLandscape } = useUAContext()
  const t = useTranslations()
  const { icon, message, children } = getAlertConfig(variant, t)

  return (
    <div
      className={cn(
        'absolute right-0 left-0 z-10 flex items-center gap-3 bg-[#131b26]/30 px-3 py-1.5 backdrop-blur-[50px]',
        isMobileLandscape
          ? 'mx-0 mt-0 mb-0 w-full rounded-none'
          : 'mx-0 mt-0 mb-0 w-full rounded-none md:mx-4 md:my-4 md:w-[calc(100%-32px)] md:rounded-[7px] md:p-2 md:px-4',
        className,
      )}
    >
      {icon && (
        <div
          className={cn('flex-shrink-0', isMobileLandscape ? 'h-4 w-4' : 'h-4 w-4 md:h-5 md:w-5')}
        >
          {icon}
        </div>
      )}
      <p className="body2-omni-styles flex-1 break-words text-white">{message}</p>
      {children?.()}
      <Button
        onClick={onClose}
        size="custom"
        className="active:[&>svg>path]:fill-vive-primary-30 z-10 h-5 w-5 flex-shrink-0 bg-transparent p-0 max-md:h-4 max-md:w-4 hover:[&>svg>path]:fill-white"
      >
        <CloseIcon className="h-full w-full" />
      </Button>
    </div>
  )
}

const TrailerAlertOverlay = ({ setCloseAlert }: { setCloseAlert: (v: boolean) => void }) => {
  const { isPreferredDevice, isVoucherCheckPassed } = useCheckEnterRoomContext()
  const showDeviceAlert = !isPreferredDevice
  const showPreCheckAlert = isVoucherCheckPassed === false

  if (!showDeviceAlert && !showPreCheckAlert) return null

  return (
    <AlertOverlay
      onClose={() => setCloseAlert(true)}
      variant={showPreCheckAlert ? 'premium' : 'device'}
    />
  )
}

export default TrailerAlertOverlay
